import { useCallback, useEffect, useRef, useState } from 'react'

import type { ErrorLike } from '@apollo/client'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import type { ResultOf } from '~publish/graphql'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { useCommentsScoreQuery } from './useCommentsScoreQuery'
import type { commentsScoreQuery } from './useCommentsScoreQuery'

type CommentsScoreForChannels = ResultOf<
  typeof commentsScoreQuery
>['commentsScoreForChannels']

/** Averaged score across all requested channels. */
export type CommentsScoreSummary = CommentsScoreForChannels['summary']

/** Score details for a single channel. */
export type ChannelScoreDetails =
  CommentsScoreForChannels['channelsScores'][number]['scoreDetails']

/** A single score metric: its raw value and percentage (0–100). */
export type CommentsScoreMetric =
  CommentsScoreSummary['currentPeriodScore']['responseRate']

export type CommentsScoreData = ChannelScoreDetails | CommentsScoreSummary

type ApolloScoreState = {
  score: CommentsScoreData | null
  loading: boolean
  error: ErrorLike | null
}

type UseCommentsScoreResult = {
  commentsScore: CommentsScoreData | null
  loading: boolean
  error: ErrorLike | null
}

/* comment events that should trigger a score update */
const commentsEvents = [
  PusherEvent.COMMENT_DISMISSED,
  PusherEvent.COMMENT_REPLIED,
  PusherEvent.COMMENT_UNDISMISSED,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
  PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
  PusherEvent.COMMENT_INGESTED,
]

/**
 * Hook to manage comments score state and real-time updates.
 *
 * @param channelIds - Channel IDs to fetch scores for. Callers must pre-filter
 *   this list to channels the actor has `viewComments` access to. The Pusher
 *   refetch guard at the bottom of this hook relies on this: it only refetches
 *   when the event's channelId is in this list, so passing an unfiltered list
 *   would allow Pusher events from inaccessible channels to trigger refetches.
 */
export function useCommentsScore(args: {
  channelIds: string[]
}): UseCommentsScoreResult {
  const organizationId = useOrganizationId()
  const { fetchCommentsScore } = useCommentsScoreQuery()
  const isFetchingRef = useRef(false)
  const hasPendingFetchRef = useRef(false)

  // Create a stable key from channelIds to detect actual changes
  const channelIdsKey = JSON.stringify(args.channelIds.toSorted())

  const [state, setState] = useState<ApolloScoreState>({
    score: null,
    loading: false,
    error: null,
  })

  const fetchAndUpdateScore = useCallbackRef(async () => {
    // Prevent concurrent requests - queue if already fetching
    if (isFetchingRef.current) {
      hasPendingFetchRef.current = true
      return
    }

    if (!organizationId) {
      setState({
        score: null,
        loading: false,
        error: new Error('Organization ID is required'),
      })
      return
    }

    isFetchingRef.current = true
    setState((prev) => ({ ...prev, loading: true }))

    try {
      const result = await fetchCommentsScore({
        organizationId,
        channelIds: args.channelIds,
      })

      // For single channel, use the channel-specific score; otherwise use summary
      const scoreData =
        args.channelIds.length === 1
          ? (result?.data?.commentsScoreForChannels.channelsScores[0]
              ?.scoreDetails ?? null)
          : (result?.data?.commentsScoreForChannels.summary ?? null)

      setState({
        score: scoreData,
        loading: false,
        error: result?.error || null,
      })
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      }))
    } finally {
      isFetchingRef.current = false

      // If a fetch was queued while this one was running, trigger it now
      if (hasPendingFetchRef.current) {
        hasPendingFetchRef.current = false
        void fetchAndUpdateScore()
      }
    }
  })

  // Reset state when channelId or organizationId changes
  useEffect(() => {
    if (!organizationId) {
      setState({
        score: null,
        loading: false,
        error: new Error('Organization Id is required'),
      })
    } else {
      // Clear score when channels change to prevent stale data
      setState((prev) => ({
        ...prev,
        score: null,
        error: null,
      }))
    }
  }, [channelIdsKey, organizationId])

  // Initial fetch. Wait until at least one channel is known: on a cold load the
  // supported-channels list is still in flight and derives to `[]`, and firing
  // the score query with an empty `channelIds` is rejected by the backend guard.
  useEffect(() => {
    if (organizationId && args.channelIds.length > 0) {
      void fetchAndUpdateScore()
    }
  }, [
    organizationId,
    fetchAndUpdateScore,
    channelIdsKey,
    args.channelIds.length,
  ])

  // Subscribe to Pusher events
  const handlePusherEvent = useCallback(
    ({ channelId }: { channelId?: string }) => {
      if (channelId && args.channelIds.includes(channelId)) {
        void fetchAndUpdateScore()
      }
    },
    [args.channelIds, fetchAndUpdateScore],
  )

  usePusherEvent(commentsEvents, handlePusherEvent)

  return {
    commentsScore: state.score,
    loading: state.loading,
    error: state.error,
  }
}
